import { useEffect, useRef, useState } from 'react';

import MonacoEditor, { Theme } from '@monaco-editor/react';
import { type editor } from 'monaco-editor';

import { Badge } from '../../primitives/badge';
import { LoadingAddon } from '../../primitives/loading-addon';
import { Skeleton } from '../../primitives/skeleton';
import { cn } from '../../utils/cn';
import { CODE_EDITOR_LANGUAGES } from './code-editor-languages';

const READONLY_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  readOnly: true,
  domReadOnly: true,
  cursorStyle: 'line-thin',
  renderLineHighlight: 'none',
};

const EDITABLE_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  readOnly: false,
  domReadOnly: false,
  cursorStyle: 'line',
  renderLineHighlight: 'all',
};

interface CodeEditorProps {
  disable?: boolean;
  height?: string | number;
  id?: string;
  isLoading?: boolean;
  language?: string;
  languageLabel?: string;
  onChange?: (value: string | undefined) => void;
  options?: editor.IStandaloneEditorConstructionOptions;
  theme?: Theme | 'sx-dark';
  value?: string;
  width?: string | number;
}

export function CodeEditor({
  disable = false,
  height = '323px',
  id,
  isLoading = false,
  language = CODE_EDITOR_LANGUAGES.json,
  languageLabel,
  onChange,
  options,
  theme = 'light',
  value,
  width = '100%',
}: CodeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [overflowWidgetsDomNode, setOverflowWidgetsDomNode] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const domNode = document.createElement('div');
    domNode.className = 'monaco-editor';
    document.body.appendChild(domNode);
    setOverflowWidgetsDomNode(domNode);

    return () => {
      domNode.remove();
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.updateOptions(disable ? READONLY_OPTIONS : EDITABLE_OPTIONS);
  }, [disable]);

  return (
    <div className="flex w-[99%] flex-col" id={id ?? undefined}>
      <Badge className="self-end rounded-b-none px-4 pt-1.5 pb-0.5 select-none" variant="secondary">
        {languageLabel ?? language.toUpperCase()}
      </Badge>
      {isLoading || !overflowWidgetsDomNode ? (
        <SkeletonCodeEditor />
      ) : (
        <MonacoEditor
          className={cn(
            'box-border rounded-lg rounded-r-none border border-input pl-3 ring-offset-background focus-within:ring-1 focus-within:ring-ring',
            theme === 'light' ? 'bg-white' : 'bg-[#1E1E1E]',
            {
              'border-input/50': disable || isLoading,
            },
          )}
          height={height}
          language={language}
          loading={<SkeletonCodeEditor />}
          onChange={onChange}
          onMount={(editor) => {
            editorRef.current = editor;
            overflowWidgetsDomNode.className = editor.getDomNode()?.className ?? 'monaco-editor';
          }}
          options={{
            automaticLayout: true,
            fixedOverflowWidgets: true,
            minimap: { enabled: false },
            overflowWidgetsDomNode,
            padding: { top: 2, bottom: 2 },
            tabSize: 2,
            wordWrap: 'on',
            wordWrapColumn: 80,
            ...(disable ? READONLY_OPTIONS : {}),
            ...options,
          }}
          theme={theme}
          value={value}
          width={width}
        />
      )}
    </div>
  );
}

function SkeletonCodeEditor() {
  return (
    <div className="relative box-border h-[323px] w-full rounded-lg rounded-r-none border border-input/50 bg-card pl-[75px]">
      <div className="animate-pulse space-y-1 py-1">
        <Skeleton className="h-3 w-[30%]" />
        <Skeleton className="h-3 w-[20%]" />
        <Skeleton className="h-3 w-[45%]" />
        <Skeleton className="h-3 w-[10%]" />
      </div>
      <LoadingAddon className="top-1" type="plain" />
    </div>
  );
}
